import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlusBold, PiMinusBold } from "react-icons/pi";
import { motion, AnimatePresence } from "framer-motion";

export default function FAQAccordion({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryColor = content.colorPalette?.primary || "#111827";
    const fontFamily = content.fontFamily || "inherit";
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const items = content.items || [];

    const updateItem = (index: number, key: string, value: string) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [key]: value };
        onContentChange?.({ ...content, items: newItems });
    };

    return (
        <section className={`px-6 py-24 bg-gray-50 ${isSelected ? "ring-2 ring-blue-500" : ""}`} style={{ fontFamily }}>
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-16">
                    <span className="text-xs font-black uppercase tracking-[0.3em] text-gray-400 mb-4 block">
                        FAQ
                    </span>
                    <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                        <EditableText
                            value={content.title}
                            onChange={(v) => onContentChange?.({ ...content, title: v })}
                            as="span"
                            placeholder="FAQ Title"
                        />
                    </h2>
                </div>

                <div className="space-y-4">
                    {items.map((item: any, i: number) => (
                        <div
                            key={i}
                            className={`rounded-2xl border transition-all duration-300 overflow-hidden ${openIndex === i ? "bg-white border-gray-200 shadow-lg" : "bg-white/60 border-transparent hover:border-gray-200"
                                }`}
                        >
                            <button
                                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                                className="w-full flex items-center justify-between p-6 md:p-8 text-left"
                            >
                                <span className="text-lg md:text-xl font-bold text-gray-900 pr-8 flex-1">
                                    <EditableText
                                        value={item.q}
                                        onChange={(v) => updateItem(i, 'q', v)}
                                        as="span"
                                        placeholder="Question?"
                                    />
                                </span>
                                <span
                                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${openIndex === i ? "text-white" : "bg-gray-100 text-gray-500"}`}
                                    style={openIndex === i ? { backgroundColor: primaryColor } : {}}
                                >
                                    {openIndex === i ? <PiMinusBold /> : <PiPlusBold />}
                                </span>
                            </button>
                            <AnimatePresence>
                                {openIndex === i && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-6 md:px-8 pb-8 pt-0 text-gray-600 leading-relaxed">
                                            <EditableText
                                                value={item.a}
                                                onChange={(v) => updateItem(i, 'a', v)}
                                                as="p"
                                                placeholder="Answer..."
                                            />
                                            {item.link && (
                                                <a
                                                    href={onContentChange ? undefined : item.link}
                                                    target={item.link.startsWith('http') && !onContentChange ? "_blank" : undefined}
                                                    rel={item.link.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                                                    className="inline-block mt-4 text-sm font-bold text-blue-600 hover:text-blue-700 hover:underline"
                                                    onClick={(e) => {
                                                        if (onContentChange) e.preventDefault();
                                                    }}
                                                >
                                                    <EditableText
                                                        value={item.linkText || "Learn more"}
                                                        onChange={(v) => updateItem(i, 'linkText', v)}
                                                        as="span"
                                                        placeholder="Link text"
                                                    />
                                                </a>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                    {isSelected && (
                        <div className="text-center mt-8">
                            <button
                                onClick={() => {
                                    const newItems = [...items, { q: "New Question?", a: "New Answer" }];
                                    onContentChange?.({ ...content, items: newItems });
                                }}
                                className="px-4 py-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors font-medium text-sm"
                            >
                                + Add Question
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
